(() => {
  const controllers = new Map();
  const thumbSelector = '[data-product-thumb], [data-combination-image-thumb]';
  const resolveGallery = (root) => root.matches?.('.product-gallery') ? root : root.querySelector('.product-gallery');
  const get = (root) => {
    const gallery = resolveGallery(root);
    if (!gallery) return null;
    const controller = controllers.get(gallery);
    return controller?.main === gallery.querySelector('.product-gallery__main') ? controller : null;
  };

  const initGallery = (gallery) => {
    if (typeof window.EmblaCarousel !== 'function') return;
    const main = gallery.querySelector('.product-gallery__main');
    if (get(gallery)?.track === main?.querySelector('.product-gallery__track')) return;
    controllers.get(gallery)?.destroy();
    const original = main?.querySelector('.product-gallery__main-image');
    const thumbs = [...gallery.querySelectorAll(thumbSelector)];
    if (!original || original.tagName !== 'IMG' || thumbs.length < 2) return;
    const combination = gallery.classList.contains('combination-gallery');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const track = document.createElement('div');
    track.className = 'product-gallery__track';
    const items = thumbs.map(thumb => ({ thumb, url: thumb.dataset.imageUrl, alt: thumb.dataset.imageAlt || '' }));
    const appendSlide = (item) => {
      const slide = document.createElement('div');
      slide.className = 'product-gallery__slide';
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'スライド');
      const image = document.createElement('img');
      image.className = 'product-gallery__main-image';
      image.width = original.width || 1600;
      image.height = original.height || 1600;
      image.alt = item.alt;
      image.decoding = 'async';
      image.draggable = false;
      image.src = item.url;
      slide.append(image);
      track.append(slide);
      Object.assign(item, { image, slide });
    };
    items.forEach(appendSlide);
    const status = document.createElement('p');
    status.className = 'product-gallery__load-status';
    status.dataset.galleryLoadStatus = '';
    status.setAttribute('role', 'status');
    status.hidden = true;
    main.replaceChildren(track, status);
    main.classList.add('product-gallery__viewport');
    main.setAttribute('role', 'region');
    main.setAttribute('aria-roledescription', 'カルーセル');
    main.setAttribute('aria-label', '商品画像');
    const initial = Math.max(0, thumbs.findIndex(thumb => thumb.getAttribute('aria-current') === 'true'));
    const api = window.EmblaCarousel(main, {
      container: track, loop: true, startIndex: initial, duration: 25, watchSlides: false,
      breakpoints: { '(prefers-reduced-motion: reduce)': { duration: 0 } },
    });
    let request = 0;
    let destroyed = false;
    const ready = (item) => {
      const token = ++request;
      if (item.image.complete && item.image.naturalWidth) {
        status.hidden = true;
        main.setAttribute('aria-busy', 'false');
        return Promise.resolve();
      }
      main.setAttribute('aria-busy', 'true');
      status.textContent = '画像を読み込み中…';
      status.hidden = false;
      return item.image.decode().then(() => {
        if (destroyed || token !== request) return;
        main.setAttribute('aria-busy', 'false');
        status.hidden = true;
      }, () => {
        if (destroyed || token !== request) return;
        main.setAttribute('aria-busy', 'false');
        status.textContent = '画像を読み込めませんでした。下の画像をもう一度選んでください。';
        status.hidden = false;
      });
    };
    const sync = () => {
      const selected = api.selectedScrollSnap();
      items.forEach((item, index) => {
        const active = index === selected;
        item.thumb.classList.toggle('is-active', active);
        item.thumb.setAttribute('aria-current', String(active));
        item.slide.setAttribute('aria-label', `${index + 1} / ${items.length}`);
        item.slide.setAttribute('aria-hidden', String(!active));
        if (combination) item.image.toggleAttribute('data-combination-main-image', active);
      });
      return ready(items[selected]);
    };
    const select = (index) => {
      const image = items[index].image;
      if (image.complete && !image.naturalWidth) image.src = items[index].url;
      api.scrollTo(index, reducedMotion.matches);
      return sync();
    };
    const click = (event) => {
      const index = items.findIndex(item => item.thumb === event.target.closest(thumbSelector));
      if (index >= 0) select(index);
    };
    gallery.addEventListener('click', click);
    api.on('select', sync);
    api.on('reInit', sync);
    const controller = {
      main, track,
      show({ url, alt = '' }) {
        if (!url) return Promise.resolve();
        let index = items.findIndex(item => item.url === url);
        if (index < 0) {
          // Keep the product photographs intact and reuse one selection-preview slide.
          let preview = items.find(item => item.thumb.hasAttribute('data-gallery-preview'));
          if (!preview) {
            const thumb = document.createElement('button');
            thumb.type = 'button';
            thumb.className = 'product-gallery__thumb combination-gallery__thumb';
            thumb.dataset.combinationImageThumb = '';
            thumb.dataset.galleryPreview = '';
            thumb.setAttribute('aria-label', '選択中のデザインを表示');
            thumb.append(document.createElement('img'));
            gallery.querySelector('.product-gallery__thumbs').append(thumb);
            preview = { thumb, url, alt };
            items.push(preview);
            appendSlide(preview);
          }
          Object.assign(preview, { url, alt });
          preview.image.src = url;
          preview.image.alt = alt;
          preview.thumb.dataset.imageUrl = url;
          preview.thumb.dataset.imageAlt = alt;
          const thumbnail = preview.thumb.querySelector('img');
          thumbnail.src = url;
          thumbnail.alt = '';
          index = items.indexOf(preview);
          api.reInit();
        }
        return select(index);
      },
      destroy() {
        destroyed = true;
        request++;
        api.destroy();
        gallery.removeEventListener('click', click);
        controllers.delete(gallery);
      },
    };
    controllers.set(gallery, controller);
    sync();
  };
  const init = (root = document) => {
    controllers.forEach((controller, gallery) => {
      if (!gallery.isConnected) controller.destroy();
    });
    if (root.matches?.('.product-gallery')) initGallery(root);
    root.querySelectorAll('.product-gallery').forEach(initGallery);
  };
  window.AgetaProductGallery = { get, init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((controller, gallery) => {
      if (event.target.contains(gallery)) controller.destroy();
    });
  });
})();
